import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import mermaid from "mermaid";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { ArrowLeft, Download, FileText, Loader2 } from "lucide-react";

const COMPONENT_DIAGRAM = `
flowchart TB
    classDef user fill:#1f2937,stroke:#60a5fa,color:#e5e7eb,stroke-width:2px
    classDef pod fill:#0f172a,stroke:#22c55e,color:#e5e7eb,stroke-width:2px
    classDef sched fill:#0f172a,stroke:#fbbf24,color:#e5e7eb,stroke-width:2px
    classDef ext fill:#1e1b4b,stroke:#a78bfa,color:#e5e7eb,stroke-width:2px
    classDef proxy fill:#3f1d1d,stroke:#f87171,color:#e5e7eb,stroke-width:2px
    classDef db fill:#1f2937,stroke:#10b981,color:#e5e7eb,stroke-width:2px

    Browser["🖥️ User Browser<br/>React 19 SPA<br/>Shadcn UI · Tailwind · axios"]:::user

    subgraph Pod["🐳 Hostinger VPS · Docker Compose (api + scheduler)"]
      direction TB
      Frontend["Frontend :3000<br/>Hot reload"]:::pod
      Backend["FastAPI Backend :8001<br/>uvicorn + supervisord"]:::pod

      subgraph Schedulers["Async Schedulers · asyncio loops"]
        direction LR
        CB["candle_breakout.py<br/>15-min · 21 slots<br/>LIVE Upstox orders<br/>09:30 → 14:00 IST"]:::sched
        OIA["oi_autopilot.py<br/>5-min · 61 slots<br/>Paper trades<br/>09:30 → 14:00 IST"]:::sched
        TOK["upstox_autologin.py<br/>TOTP daily refresh<br/>03:35 IST"]:::sched
      end

      subgraph Domain["Domain Modules"]
        direction LR
        MD["market_data.py<br/>Section 1"]:::pod
        OIS["oi_suggestions.py<br/>Section 2"]:::pod
        RC["risk_calculator.py<br/>Dynamic TP/SL"]:::pod
        ORD["upstox_orders.py<br/>LIVE orders"]:::pod
        ALR["alerts.py<br/>Telegram dispatch"]:::pod
      end

      Mongo[("MongoDB :27017<br/>PVC-backed<br/>9 collections")]:::db
    end

    subgraph Upstox["Upstox API"]
      UpData["v2/v3 Data<br/>Option chain · LTP · Candles · WS"]:::ext
      UpHFT["HFT Order API<br/>BUY/SELL/Positions"]:::ext
    end

    YFin["Yahoo Finance<br/>^NSEI fallback"]:::ext
    TG["Telegram Bot API<br/>Entry · TP/SL · EOD"]:::ext
    Proxy["TradeSteady Proxy<br/>151.242.178.103:49155<br/>₹499/mo · Residential IP"]:::proxy

    Browser -->|"HTTPS<br/>/api/*<br/>WebSocket /api/ws/live"| Frontend
    Frontend -.->|"Same pod<br/>internal routing"| Backend
    Backend --> Mongo
    Backend --> Schedulers
    Backend --> Domain

    CB --> ORD
    CB --> Mongo
    OIA --> OIS
    OIA --> Mongo
    OIS --> Mongo

    OIS --> UpData
    CB --> UpData
    MD --> UpData
    MD -.->|"only if Upstox down"| YFin
    TOK --> UpData

    ORD ==>|"Routed via static IP"| Proxy
    Proxy ==>|"Whitelisted"| UpHFT

    Backend -->|"Bot token"| TG
    ALR --> TG
`;

const NETWORK_DIAGRAM = `
flowchart LR
    classDef user fill:#1f2937,stroke:#60a5fa,color:#e5e7eb,stroke-width:2px
    classDef ingress fill:#0f172a,stroke:#22c55e,color:#e5e7eb,stroke-width:2px
    classDef pod fill:#0f172a,stroke:#22c55e,color:#e5e7eb,stroke-width:2px
    classDef nat fill:#374151,stroke:#9ca3af,color:#e5e7eb,stroke-width:2px
    classDef ext fill:#1e1b4b,stroke:#a78bfa,color:#e5e7eb,stroke-width:2px
    classDef proxy fill:#3f1d1d,stroke:#f87171,color:#e5e7eb,stroke-width:2px
    classDef order fill:#3f1d1d,stroke:#f87171,color:#e5e7eb,stroke-width:2px

    User["🖥️ User Browser"]:::user
    Ingress["nginx (native) + SSL<br/>stoxtrade.tech"]:::ingress
    Pod["VPS · Backend :8001"]:::pod
    NAT["VPS Static Egress IP<br/>200.141.14.58"]:::nat
    Proxy["TradeSteady Proxy<br/>Static IP 151.242.178.103<br/>Indian Residential"]:::proxy

    UpData["api.upstox.com<br/>(option chain, LTP, candles)"]:::ext
    UpOrder["api-hft.upstox.com<br/>(orders / positions)<br/>⚠️ IP whitelist enforced"]:::order
    YFin["query2.finance.yahoo.com"]:::ext
    TG["api.telegram.org"]:::ext
    InstDump["assets.upstox.com<br/>(daily instruments.json.gz)"]:::ext

    User -->|"HTTPS"| Ingress
    Ingress -->|"/api/* → :8001<br/>/ → :3000"| Pod

    Pod ==>|"DATA path<br/>(99% of calls)"| NAT
    NAT --> UpData
    NAT --> YFin
    NAT --> TG
    NAT --> InstDump

    Pod ==>|"ORDER path only<br/>httpx(proxy=...)"| Proxy
    Proxy --> UpOrder
`;

const SEQUENCE_DIAGRAM = `
sequenceDiagram
    autonumber
    participant S as candle_breakout<br/>Scheduler
    participant UD as Upstox Data
    participant DB as MongoDB
    participant TS as TradeSteady Proxy
    participant UO as Upstox Order API
    participant TG as Telegram

    Note over S: Every 5 min · 09:30-12:00 IST<br/>native F&O-universe breakout scan
    S->>UD: 5-min candles for full F&O universe<br/>(native_breakout_scanner.py, Phase 35)
    UD-->>S: Breakout matches
    S->>UD: Option chain + ATM + ATR + Sector
    UD-->>S: Strike + Greeks + lot_size
    S->>DB: Persist run + picks
    S->>S: Compute dynamic TP/SL<br/>(sector + market + ATR)

    Note over S: Auto-entry path
    S->>S: Check daily cap (≤2)<br/>+ live_trading flag<br/>+ 14:00 cutoff

    S->>TS: httpx(proxy=URL) BUY LIMIT<br/>@ LTP −5%
    TS->>UO: Routed via static IP<br/>151.242.178.103
    UO-->>TS: order_id
    TS-->>S: ok=true
    S->>DB: Persist trade<br/>(live_buy_order_id, etc.)
    S->>TG: 🤖 LIVE BUY · symbol · slot

    Note over S: P&L refresh every 60s
    loop Auto-close on TP / SL / MTF reversal / 15:15 IST
        S->>UD: Current ATM LTP
        S->>S: Compare vs target_pct / stop_loss_pct
        alt TP hit (pnl ≥ target_pct)
            S->>TS: SELL LIMIT @ LTP +0.25%
            S->>DB: close_reason = PROFIT_BOOKED
            S->>TG: 🎯 Profit Booked
        else SL hit (pnl ≤ stop_loss_pct)
            S->>TS: SELL LIMIT @ LTP +0.25%
            S->>DB: close_reason = STOP_LOSS
            S->>TG: 🛑 Stop Loss
        else Multi-TF reversal (entry MTF==3 AND current MTF≤1)
            S->>UD: Recompute multi-TF (5m/1h/1d)
            S->>TS: SELL LIMIT @ LTP +0.25%
            S->>DB: close_reason = MULTI_TF_REVERSAL
            S->>TG: 🔄 Multi-TF Reversal Exit
        else EOD reached (15:15 IST)
            S->>TS: SELL LIMIT @ LTP +0.25%
            S->>DB: close_reason = EOD
            S->>TG: 📊 EOD close
        end
    end
`;

const AUTOMATION_DIAGRAM = `
flowchart TB
    classDef trigger fill:#0f172a,stroke:#fbbf24,color:#e5e7eb,stroke-width:2px
    classDef decision fill:#1f2937,stroke:#60a5fa,color:#e5e7eb,stroke-width:2px
    classDef exit fill:#3f1d1d,stroke:#f87171,color:#e5e7eb,stroke-width:2px
    classDef ok fill:#0f3025,stroke:#22c55e,color:#e5e7eb,stroke-width:2px
    classDef api fill:#1e1b4b,stroke:#a78bfa,color:#e5e7eb,stroke-width:2px

    Tick["⏱️ Scheduler tick · every 60s<br/>candle_breakout + oi_autopilot loops"]:::trigger
    Open[("Fetch all OPEN trades<br/>candle_breakout_paper_trades<br/>oi_autopilot_paper_trades")]:::api
    Tick --> Open
    Open --> ForEach{"For each OPEN trade"}:::decision

    ForEach --> A1["GET /v2/option/chain<br/>(Upstox · current ATM LTP)"]:::api
    A1 --> PNL["Compute pnl_pct =<br/>(curr_ltp − entry_ltp) / entry_ltp · 100"]:::decision

    PNL --> R1{"pnl_pct ≥<br/>target_pct?"}:::decision
    R1 -->|Yes| TP["close_reason =<br/>PROFIT_BOOKED"]:::ok
    R1 -->|No| R2{"pnl_pct ≤<br/>stop_loss_pct?"}:::decision

    R2 -->|Yes| SL["close_reason =<br/>STOP_LOSS"]:::exit
    R2 -->|No| R3{"entry MTF == 3?<br/>(only those qualify)"}:::decision

    R3 -->|No| Update["Update current LTP +<br/>pnl_pct on trade row"]:::api
    R3 -->|Yes| MTFCALL["analyze_multi_timeframe()<br/>5m / 1h / 1d trend"]:::api
    MTFCALL --> R4{"current MTF ≤ 1?"}:::decision
    R4 -->|No| Update
    R4 -->|Yes| MTFEXIT["close_reason =<br/>MULTI_TF_REVERSAL"]:::exit

    TP --> SELL
    SL --> SELL
    MTFEXIT --> SELL

    SELL{"trade.live_trade == True?"}:::decision
    SELL -->|Yes| POS["GET /v2/portfolio/short-term-positions<br/>(validate position exists)"]:::api
    SELL -->|No| MARK
    POS --> HASPOS{"position exists?"}:::decision
    HASPOS -->|No| MARKSKIP["sell_skipped_reason =<br/>no_position"]:::exit
    HASPOS -->|Yes| ORDER["POST /v2/order/place<br/>via TradeSteady proxy<br/>SELL LIMIT @ LTP +0.25%"]:::api
    ORDER --> CONFIRM["GET /v2/order/details<br/>poll up to 5×<br/>(complete / open / rejected)"]:::api
    CONFIRM --> MARK

    MARKSKIP --> MARK
    MARK[("UPDATE trade row<br/>status=CLOSED, close_reason,<br/>close_ce_ltp, final_pnl_pct,<br/>live_sell_order_id, closed_at")]:::ok

    MARK --> TG["Telegram dispatch<br/>🎯 / 🛑 / 🔄 / 📊<br/>(unless alerts_paused)"]:::api
    MARK --> AUDIT["live_trading_shared.list_today_live_orders()<br/>GET /api/live-orders/audit<br/>aggregates both sections"]:::api

    Update -.->|loop| ForEach
`;

const BUY_WORKFLOW = `
sequenceDiagram
    autonumber
    participant U as Upstream Source
    participant SCAN as Scanner<br/>(15-min CB / 5-min OI)
    participant ENRICH as _enrich() / pick build
    participant VAL as Entry Validator
    participant DB as MongoDB
    participant TS as TradeSteady<br/>HTTPS Proxy
    participant UP as Upstox HFT<br/>/v2/order/place
    participant POLL as confirm_order_accepted<br/>(poll /order/details)
    participant TG as Telegram

    Note over U,POLL: BUY workflow — same shape for Section 5 (CB) & Section 6 (OI)
    SCAN->>U: Native breakout scan / Upstox OI feed
    U-->>SCAN: candidates[]
    SCAN->>ENRICH: For each candidate
    ENRICH->>ENRICH: Compute multi_tf, sector, ATR<br/>target_pct, stop_loss_pct,<br/>anticipated_profit_pct
    ENRICH-->>SCAN: enriched picks (sorted)

    SCAN->>VAL: Top pick
    VAL->>VAL: 1. Multi-TF gate (CB needs 3-of-3 / OI at least 2-of-3)
    VAL->>VAL: 2. Score gate (OI: score 5.0+ AND VWAP distance under 5%)
    VAL->>VAL: 3. Sector trend not BEARISH
    VAL->>VAL: 4. Time gate (now before 14:00 IST)
    VAL->>DB: 5. Daily cap check (auto_entered today below cap)
    VAL->>DB: 6. Cross-section dedup via has_live_buy_today(symbol)
    VAL->>DB: 7. Live trading kill-switch enabled in settings
    alt Any validation fails
        VAL-->>SCAN: SKIP + log reason
    else All pass
        VAL-->>SCAN: PROCEED
    end

    SCAN->>SCAN: 8. Limit price calc<br/>buy_limit = LTP × (1 − 5%)<br/>rounded to ₹0.05 tick

    alt live_trading == True
        SCAN->>TS: POST /v2/order/place<br/>SIDE=BUY · LIMIT · qty=lot_size<br/>product=D (Delivery)
        TS->>UP: forward via static IP
        UP-->>TS: {order_id, status: "validation pending"}
        TS-->>SCAN: order_id
        SCAN->>POLL: Poll /order/details ×4
        loop up to 4 polls × 0.6s
            POLL->>UP: GET /v2/order/details?order_id
            UP-->>POLL: {status, filled_qty, avg_price}
            alt status complete or open
                POLL-->>SCAN: accepted=true
            else status rejected or cancelled
                POLL-->>SCAN: accepted=false (rejected)
            end
        end
        alt accepted (status complete or open)
            SCAN->>DB: INSERT trade row<br/>live_trade=True<br/>live_buy_order_id<br/>live_broker_status<br/>live_filled_qty / avg_price<br/>order_status, order_type=LIMIT
            SCAN->>TG: 🤖 Auto-entered LIVE · symbol
        else rejected or cancelled
            SCAN->>DB: log rejection<br/>(no trade row)
            SCAN->>TG: ⚠️ BUY rejected · reason
        end
    else paper only
        SCAN->>DB: INSERT trade row<br/>live_trade=False<br/>atm_ce_ltp = limit_price<br/>order_status="PAPER_OPEN"
        SCAN->>TG: 🤖 Auto-entered (Paper)
    end

    Note over SCAN,DB: Trade is now OPEN — monitored every 60s by refresh loop
`;

const SELL_WORKFLOW = `
sequenceDiagram
    autonumber
    participant SCH as Scheduler<br/>(60s tick)
    participant REFRESH as _refresh_open_*
    participant UD as Upstox Data<br/>/v2/option/chain
    participant MTF as analyze_multi_timeframe<br/>(5m / 1h / 1d)
    participant GUARD as is_buy_filled +<br/>position_exists guards
    participant TS as TradeSteady Proxy
    participant UP as Upstox HFT<br/>SELL · CANCEL · POSITIONS
    participant DB as MongoDB
    participant TG as Telegram

    Note over SCH,TG: SELL / EXIT workflow — same shape for Section 5 & Section 6
    SCH->>REFRESH: Tick (every 60s)
    REFRESH->>DB: SELECT status=OPEN AND entered_at within last 2 days
    DB-->>REFRESH: open_trades[]

    loop For each open trade
        alt trade.live_trade == True
            REFRESH->>UP: GET /v2/order/details<br/>(refresh_buy_status_if_open)
            UP-->>REFRESH: latest broker status
            REFRESH->>DB: persist status drift<br/>(live_broker_status,<br/>live_filled_qty,<br/>live_avg_fill_price)
        end

        REFRESH->>UD: build_option_recommendation<br/>(symbol → ATM CE LTP)
        UD-->>REFRESH: curr_ltp
        REFRESH->>REFRESH: pnl_pct = (curr − entry) / entry × 100<br/>UPDATE current_ce_ltp, pnl_pct

        alt live AND NOT is_buy_filled(trade)
            REFRESH-->>SCH: SKIP auto-close<br/>(naked-short guard)
        else
            REFRESH->>REFRESH: Evaluate exit conditions<br/>(priority order)

            alt pnl_pct hits target_pct
                REFRESH->>REFRESH: close_reason = PROFIT_BOOKED
            else pnl_pct hits stop_loss_pct
                REFRESH->>REFRESH: close_reason = STOP_LOSS
            else entry_mtf == 3 (CB only)
                REFRESH->>MTF: analyze_multi_timeframe(symbol)
                MTF-->>REFRESH: current_mtf.bullish_count
                alt current_mtf dropped to 1 or 0
                    REFRESH->>REFRESH: close_reason = MULTI_TF_REVERSAL<br/>exit_multi_tf_count = N
                end
            else
                REFRESH-->>SCH: HOLD<br/>(mark-to-market only)
            end

            alt close_reason set
                Note over REFRESH,GUARD: Place SELL — gated by 2 safety checks
                REFRESH->>GUARD: 1. is_buy_filled(trade)?
                GUARD-->>REFRESH: yes (refreshed status)
                REFRESH->>GUARD: 2. position_exists(instrument_key)?
                GUARD->>UP: GET /v2/portfolio/short-term-positions
                UP-->>GUARD: positions[]
                GUARD-->>REFRESH: ok (net_qty at least lot_size)

                REFRESH->>REFRESH: sell_limit = LTP × (1 + 0.25%)
                REFRESH->>TS: POST /v2/order/place<br/>SIDE=SELL · LIMIT
                TS->>UP: forward
                UP-->>TS: sell_order_id returned
                TS-->>REFRESH: ok

                REFRESH->>DB: UPDATE status=CLOSED<br/>close_reason,<br/>close_ce_ltp, final_pnl_pct,<br/>live_sell_order_id, closed_at
                REFRESH->>TG: 🎯 / 🛑 / 🔄 close message
            end
        end
    end

    Note over SCH,TG: At 15:15 IST — EOD path triggers
    SCH->>REFRESH: _maybe_auto_close_eod()
    loop For each remaining OPEN trade
        alt live AND BUY not filled
            REFRESH->>UP: DELETE /v2/order/cancel<br/>(cancel orphan BUY)
            UP-->>REFRESH: cancelled
            REFRESH->>DB: close_reason = BUY_CANCELLED_EOD<br/>final_pnl_pct = 0%
        else
            REFRESH->>UP: SELL LIMIT @ LTP +0.25%
            REFRESH->>DB: close_reason = EOD
        end
    end
    REFRESH->>TG: 📊 EOD summary
`;

const LIFECYCLE_DIAGRAM = `
stateDiagram-v2
    [*] --> SignalGenerated: Native breakout scan / OI feed
    SignalGenerated --> Validated: Entry validator (7 gates)
    SignalGenerated --> Rejected: Any gate fails<br/>(no DB row)
    Rejected --> [*]

    Validated --> PaperOpen: live=False
    Validated --> PendingBuy: live=True

    state PendingBuy {
        [*] --> OrderPlaced
        OrderPlaced --> AwaitingFill: status="open"
        AwaitingFill --> AwaitingFill: refresh / 60s
        AwaitingFill --> LiveOpen: status="complete"<br/>filled_qty == lot_size
        AwaitingFill --> BrokerRejected: status="rejected"
        AwaitingFill --> Cancelled: EOD cancel<br/>(orphan BUY)
    }

    PaperOpen --> Monitoring: mark-to-market loop
    LiveOpen --> Monitoring: mark-to-market loop

    state Monitoring {
        [*] --> EvaluateExits
        EvaluateExits --> EvaluateExits: 60s refresh<br/>HOLD if no condition met
        EvaluateExits --> ProfitBooked: pnl >= target_pct
        EvaluateExits --> StopLoss: pnl <= stop_loss_pct
        EvaluateExits --> MtfReversal: entry_mtf==3<br/>current_mtf<=1
        EvaluateExits --> ManualExitTriggered: User clicks Exit
        EvaluateExits --> EodReached: 15:15 IST
    }

    ProfitBooked --> SellPlaced: live_trade?
    StopLoss --> SellPlaced: live_trade?
    MtfReversal --> SellPlaced: live_trade?
    ManualExitTriggered --> SellPlaced: live_trade?
    EodReached --> SellPlaced: live_trade?

    state SellPlaced {
        [*] --> BuyFilledCheck
        BuyFilledCheck --> NakedShortBlocked: is_buy_filled = False
        BuyFilledCheck --> PositionExistsCheck: is_buy_filled = True
        PositionExistsCheck --> NoPosition: positions has no long
        PositionExistsCheck --> SellPosted: long >= lot_size
        SellPosted --> Sold: order_id returned
    }

    Cancelled --> Closed
    BrokerRejected --> Closed
    NakedShortBlocked --> Closed: sell_skipped_reason=buy_not_filled
    NoPosition --> Closed: sell_skipped_reason=no_position
    Sold --> Closed
    PaperOpen --> Closed: paper exit
    Closed --> AuditLog
    AuditLog --> Telegram
    Telegram --> [*]
`;

const DIAGRAMS = [
  { id: "component", title: "1 · Component View", code: COMPONENT_DIAGRAM, desc: "How the pieces fit together — browser, pod, schedulers, domain modules, MongoDB, and every external service the app talks to." },
  { id: "network", title: "2 · Network · Egress Paths", code: NETWORK_DIAGRAM, desc: "Why orders go through TradeSteady and data does not. Two distinct egress paths, one shared NAT IP that rotates, one static residential IP whitelisted at Upstox." },
  { id: "sequence", title: "3 · Live Trade Sequence", code: SEQUENCE_DIAGRAM, desc: "Step-by-step lifecycle of a single Section 5 live trade — scan, qualify, dynamic TP/SL, BUY via proxy, P&L refresh, all four exit triggers (TP, SL, Multi-TF reversal, EOD)." },
  { id: "automation", title: "4 · Automated Exit Logic · API Sequence", code: AUTOMATION_DIAGRAM, desc: "Per-trade exit pipeline that runs every 60s on each OPEN paper or live trade. Shows the priority chain (TP → SL → Multi-TF reversal 3→1), the Upstox API calls hit at each step, position-existence validation before SELL, and how exits land in the live-order audit log." },
  { id: "buy-workflow", title: "5 · BUY Workflow · End-to-End", code: BUY_WORKFLOW, desc: "Full BUY execution path for both Section 5 (Candle Breakout) and Section 6 (OI Long Buildup). Covers signal generation, the 7-gate entry validator (multi-TF, score, sector, time, daily cap, cross-section dedup, kill-switch), LTP −5% limit price calc, Upstox order place + 4× /order/details poll, paper-vs-live branching, and trade-row persistence." },
  { id: "sell-workflow", title: "6 · SELL / EXIT Workflow · End-to-End", code: SELL_WORKFLOW, desc: "Full exit path covering the 60s refresh loop. Priority chain: PROFIT_BOOKED → STOP_LOSS → MULTI_TF_REVERSAL (3→1) → HOLD. Both safety guards (is_buy_filled + position_exists) before any SELL is sent. EOD branch shows orphan-BUY cancellation vs SELL based on fill status." },
  { id: "lifecycle", title: "7 · Trade Lifecycle State Machine", code: LIFECYCLE_DIAGRAM, desc: "Every state a single trade can be in, from signal generation through PendingBuy / LiveOpen / PaperOpen / Monitoring / SellPlaced / Closed. Distinct terminal reasons: ProfitBooked, StopLoss, MtfReversal, ManualExit, EOD, BuyCancelledEOD, NakedShortBlocked, NoPosition, BrokerRejected." },
];

mermaid.initialize({
  startOnLoad: false,
  theme: "dark",
  themeVariables: {
    background: "#0a0a0f",
    primaryColor: "#1f2937",
    primaryTextColor: "#e5e7eb",
    primaryBorderColor: "#60a5fa",
    lineColor: "#475569",
    secondaryColor: "#1e1b4b",
    tertiaryColor: "#0f172a",
    fontFamily: "JetBrains Mono, ui-monospace, monospace",
    fontSize: "13px",
  },
  flowchart: { curve: "basis", htmlLabels: true, padding: 12, useMaxWidth: true },
  sequence: { actorMargin: 60, messageMargin: 36, wrap: true, useMaxWidth: true },
});

const DiagramCard = ({ diagram, onSvgReady }) => {
  const ref = useRef(null);
  const [svg, setSvg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { svg } = await mermaid.render(`m-${diagram.id}-${Date.now()}`, diagram.code);
        if (!cancelled) {
          setSvg(svg);
          if (onSvgReady) onSvgReady(diagram.id, svg);
        }
      } catch (e) {
        if (!cancelled) {
          setSvg(`<pre style="color:#f87171">Failed to render: ${e?.message || e}</pre>`);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [diagram, onSvgReady]);

  const onDownload = () => {
    if (!svg) return;
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sniper-trade-${diagram.id}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section
      className="border-grid surface p-6 md:p-8 space-y-4"
      data-testid={`arch-card-${diagram.id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
            {diagram.title}
          </h2>
          <p className="text-xs text-muted-2 mt-1 max-w-3xl">{diagram.desc}</p>
        </div>
        <button
          onClick={onDownload}
          data-testid={`arch-download-${diagram.id}`}
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-wider font-mono-data px-3 py-1.5 border border-grid hover:bg-white/5"
        >
          <Download size={12} />
          Download SVG
        </button>
      </div>
      <div
        ref={ref}
        className="overflow-x-auto border border-grid p-4 bg-[#0a0a0f]"
        style={{ minHeight: 200 }}
        dangerouslySetInnerHTML={{ __html: svg || `<div class="text-muted-3 text-xs font-mono-data p-4">Rendering...</div>` }}
      />
    </section>
  );
};

// --- Helper: rasterize a single SVG string to a PNG data URL at the requested width ---
const svgToPng = (svgString, targetWidthPx = 1600) =>
  new Promise((resolve, reject) => {
    // Make sure the SVG carries explicit width/height attributes — Mermaid sometimes uses viewBox only
    let xml = svgString;
    if (!/<svg[^>]*\swidth=/.test(xml)) {
      const vb = xml.match(/viewBox="([\d.\s-]+)"/);
      if (vb) {
        const [, , vw, vh] = vb[1].split(/\s+/).map(Number);
        const ratio = vh / vw;
        xml = xml.replace("<svg", `<svg width="${targetWidthPx}" height="${Math.round(targetWidthPx * ratio)}"`);
      }
    }
    const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ratio = (img.naturalHeight || 600) / (img.naturalWidth || targetWidthPx);
        canvas.width = targetWidthPx;
        canvas.height = Math.round(targetWidthPx * ratio);
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#0a0a0f";  // match the in-card background
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const pngDataUrl = canvas.toDataURL("image/png");
        URL.revokeObjectURL(url);
        resolve({ pngDataUrl, width: canvas.width, height: canvas.height });
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

export default function Architecture() {
  const cards = useMemo(() => DIAGRAMS, []);
  // Track SVGs as they render so the "Download All PDF" button can package them
  const svgMapRef = useRef({});
  const [readyCount, setReadyCount] = useState(0);
  const [exporting, setExporting] = useState(false);

  const onSvgReady = useCallback((id, svg) => {
    if (!svg) return;
    if (svgMapRef.current[id] !== svg) {
      svgMapRef.current[id] = svg;
      setReadyCount((c) => c + 1);
    }
  }, []);

  const onDownloadAllPdf = useCallback(async () => {
    setExporting(true);
    try {
      const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 36;
      const contentW = pageW - margin * 2;

      // Cover page
      pdf.setFillColor(10, 10, 15);
      pdf.rect(0, 0, pageW, pageH, "F");
      pdf.setTextColor(229, 231, 235);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(28);
      pdf.text("Sniper.Trade", margin, 110);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(18);
      pdf.text("System Architecture & Auto-Trading Workflow", margin, 140);
      pdf.setFontSize(11);
      pdf.setTextColor(148, 163, 184);
      pdf.text(`Generated ${new Date().toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST`, margin, 168);
      pdf.text(`${cards.length} diagrams · Component · Network · Live Trade · Exit Logic · BUY · SELL · Lifecycle`, margin, 188);
      pdf.setDrawColor(34, 197, 94);
      pdf.setLineWidth(2);
      pdf.line(margin, 210, margin + 80, 210);

      for (let i = 0; i < cards.length; i++) {
        const d = cards[i];
        const svgString = svgMapRef.current[d.id];
        if (!svgString) continue;   // not yet rendered

        // Rasterize the SVG at high resolution so the PDF stays crisp
        let raster;
        try {
          raster = await svgToPng(svgString, 2200);
        } catch (e) {
          console.error("svg->png failed for", d.id, e);
          continue;
        }

        pdf.addPage();
        pdf.setFillColor(10, 10, 15);
        pdf.rect(0, 0, pageW, pageH, "F");
        // Header
        pdf.setTextColor(229, 231, 235);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(16);
        pdf.text(d.title, margin, margin + 14);
        // Description (wrap)
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(148, 163, 184);
        const wrapped = pdf.splitTextToSize(d.desc, contentW);
        pdf.text(wrapped, margin, margin + 32);
        const textBottom = margin + 32 + wrapped.length * 11;

        // Embed PNG: fit inside the remaining page space, maintain aspect
        const imgTop = textBottom + 16;
        const availableH = pageH - imgTop - margin;
        const aspect = raster.height / raster.width;
        let renderW = contentW;
        let renderH = renderW * aspect;
        if (renderH > availableH) {
          renderH = availableH;
          renderW = renderH / aspect;
        }
        const x = (pageW - renderW) / 2;
        pdf.addImage(raster.pngDataUrl, "PNG", x, imgTop, renderW, renderH);

        // Footer
        pdf.setFontSize(8);
        pdf.setTextColor(75, 85, 99);
        pdf.text(`Sniper.Trade · Diagram ${i + 1} / ${cards.length}`, margin, pageH - 14);
        pdf.text(`Page ${i + 2}`, pageW - margin - 30, pageH - 14);
      }

      const filename = `sniper-trade-architecture-${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(filename);
      toast.success(`Architecture PDF downloaded · ${cards.length} diagrams`);
    } catch (e) {
      console.error("PDF export failed", e);
      toast.error("Failed to build PDF — try again or check console");
    } finally {
      setExporting(false);
    }
  }, [cards]);

  const allReady = readyCount >= cards.length;

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-primary)" }}>
      <header className="border-b border-grid px-4 md:px-8 py-4">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="arch-back-link"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] font-mono-data text-muted-2 hover:text-white"
          >
            <ArrowLeft size={14} />
            Back to Dashboard
          </Link>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data">
            Sniper.Trade · Architecture
          </div>
        </div>
      </header>

      <main className="px-4 md:px-8 py-8 md:py-12 space-y-10 max-w-[1600px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-2">
              Documentation
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-bold tracking-tight">
              System Architecture & Network Diagram
            </h1>
            <p className="text-sm text-muted-2 mt-3 max-w-3xl">
              How the auto-pilot platform is wired end-to-end — from your browser
              to NSE order books — and why a residential static-IP proxy sits in
              the order path while data flows freely from the cloud pod.
            </p>
          </div>
          <button
            onClick={onDownloadAllPdf}
            disabled={!allReady || exporting}
            data-testid="arch-download-all-pdf"
            title={
              !allReady
                ? `Waiting for diagrams to render (${readyCount}/${cards.length})…`
                : "Download every diagram on this page as a single landscape PDF"
            }
            className="self-start md:self-end inline-flex items-center gap-2 text-[10px] uppercase tracking-wider font-mono-data px-4 py-2 border disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              color: allReady && !exporting ? "var(--bull)" : "var(--text-muted)",
              borderColor: allReady && !exporting ? "var(--bull)" : "var(--border-color)",
            }}
          >
            {exporting ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <FileText size={13} />
            )}
            {exporting
              ? "Building PDF…"
              : allReady
              ? `Download all ${cards.length} diagrams · PDF`
              : `Rendering ${readyCount}/${cards.length}…`}
          </button>
        </div>

        {cards.map((d) => (
          <DiagramCard key={d.id} diagram={d} onSvgReady={onSvgReady} />
        ))}

        <section className="border-grid surface p-6 md:p-8 space-y-4" data-testid="arch-stack-card">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
            Tech Stack
          </h2>
          <div className="grid md:grid-cols-2 gap-x-8 gap-y-6 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-2">Frontend</div>
              <ul className="text-muted-2 space-y-1 list-none font-mono-data text-xs">
                <li>React 19 + Vite</li>
                <li>Shadcn UI · Tailwind · lucide-react</li>
                <li>axios + sonner</li>
                <li>Native WebSocket · 60s polling fallback</li>
                <li>Mermaid (this page)</li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-2">Backend</div>
              <ul className="text-muted-2 space-y-1 list-none font-mono-data text-xs">
                <li>FastAPI (async) · uvicorn · supervisord</li>
                <li>motor (async MongoDB) · httpx</li>
                <li>pandas + numpy · custom indicators</li>
                <li>asyncio schedulers (no Celery)</li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-2">Data sources</div>
              <ul className="text-muted-2 space-y-1 list-none font-mono-data text-xs">
                <li>Upstox v2/v3 — option chain · LTP · candles · WS · orders</li>
                <li>Yahoo Finance — Nifty fallback only</li>
                <li>Telegram Bot — trade alerts</li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-2">Infra</div>
              <ul className="text-muted-2 space-y-1 list-none font-mono-data text-xs">
                <li>Hostinger VPS (KVM2) · Docker Compose</li>
                <li>Native MongoDB on VPS</li>
                <li>TradeSteady residential proxy — ₹499/mo</li>
                <li>Upstox OAuth2 + TOTP auto-refresh</li>
              </ul>
            </div>
          </div>
        </section>

        <footer className="border-t border-grid pt-6 mt-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
            Sniper.Trade — Architecture v1
          </div>
          <Link
            to="/"
            data-testid="arch-back-link-footer"
            className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data hover:text-white"
          >
            ← Back to Dashboard
          </Link>
        </footer>
      </main>
    </div>
  );
}
